"use client";

import { useEffect, useState, useId } from "react";

/**
 * Extract YouTube video/playlist ID and type from various URL formats
 * Supports: youtube.com/watch?v=, youtu.be/, youtube.com/embed/, youtube.com/v/, youtube.com/playlist?list=
 */
function extractYoutubeData(url) {
    if (!url) return { type: null, id: null };

    // Handle playlist URLs
    const playlistMatch = url.match(/[?&]list=([a-zA-Z0-9_-]+)/);
    if (playlistMatch && !url.includes("watch?v=")) {
        return { type: "playlist", id: playlistMatch[1] };
    }

    // Handle youtu.be short URLs
    const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
    if (shortMatch) return { type: "video", id: shortMatch[1] };

    // Handle youtube.com/embed/ URLs
    const embedMatch = url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
    if (embedMatch) return { type: "video", id: embedMatch[1] };

    // Handle youtube.com/v/ URLs
    const vMatch = url.match(/youtube\.com\/v\/([a-zA-Z0-9_-]{11})/);
    if (vMatch) return { type: "video", id: vMatch[1] };

    // Handle youtube.com/watch?v= URLs (with or without additional params)
    const watchMatch = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
    if (watchMatch) return { type: "video", id: watchMatch[1] };

    // Fallback: try to get anything after v= (legacy behavior)
    const legacyMatch = url.split("v=")[1];
    if (legacyMatch) return { type: "video", id: legacyMatch.split("&")[0] };

    return { type: null, id: null };
}

function Youtube({ attributes }) {
    const [height, setHeight] = useState(350);
    const uniqueId = useId();
    const frameId = `jlt-youtube-video-${uniqueId}`;

    const { type, id } = extractYoutubeData(attributes?.url);

    useEffect(() => {
        const youtubeFrame = document.getElementById(frameId);
        if (!youtubeFrame) return;

        const offsetWidth = youtubeFrame.offsetWidth;

        const calculateHeight = (width) => {
            const aspectRatio = 16 / 9;
            return parseFloat((width / aspectRatio).toFixed(2));
        };

        setHeight(calculateHeight(offsetWidth));
    }, [frameId]);

    // Don't render if no valid ID
    if (!id || !type) {
        return (
            <div className="jlt-youtube-error" style={{ padding: "20px", background: "#f0f0f0", textAlign: "center" }}>
                Invalid YouTube URL
            </div>
        );
    }

    // Generate embed URL based on type
    const embedUrl = type === "playlist"
        ? `https://www.youtube.com/embed/videoseries?list=${id}`
        : `https://www.youtube.com/embed/${id}`;

    return (
        <iframe
            id={frameId}
            width="100%"
            height={height || 350}
            src={embedUrl}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
        />
    );
}

export default Youtube;
